import { updateDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { NotificationSettings } from '../types/Notification';

/**
 * SMS utility functions
 * In a real application, this would connect to your SMS service provider
 * Currently just provides formatting, validation, and stub functions that log to console
 */

interface SMSResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Format a phone number to a standardized format
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters except for + at the beginning
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // Add + if it doesn't exist and there's no country code
  if (!phoneNumber.startsWith('+')) {
    // Assume US number if no country code
    if (cleaned.length === 10) {
      cleaned = `1${cleaned}`; // Add US country code
    }
    cleaned = `+${cleaned}`;
  }
  
  return cleaned;
};

/**
 * Validate a phone number
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // Basic validation: phone numbers should have at least 10 digits (not including country code)
  // and a maximum of 15 digits (including country code) according to international standard
  
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Check length
  return digits.length >= 10 && digits.length <= 15;
};

/**
 * Send an SMS message
 * This is a stub function for demonstration
 */
export const sendSMS = async (phoneNumber: string, message: string): Promise<SMSResponse> => {
  try {
     // In a real application, this would call your SMS service API

     return {
        success: true,
        messageId: `sms_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
     };
  } catch (error) {
    console.error('Error sending SMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error sending SMS'
    };
  }
};

/**
 * Send a trip invitation via SMS
 */
export const sendSMSInvite = async (phoneNumber: string, message: string): Promise<SMSResponse> => {
  // Format the phone number before sending
  const formattedNumber = formatPhoneNumber(phoneNumber);
  
  // Validate the phone number
  if (!isValidPhoneNumber(formattedNumber)) {
    return {
      success: false,
      error: 'Invalid phone number format'
    };
  }
  
  // Send the SMS
  return sendSMS(formattedNumber, message);
};

/**
 * Save a user's phone number to their notification settings
 */
export const saveUserPhoneNumber = async (
  userId: string,
  phoneNumber: string,
  enableSMS: boolean = true
): Promise<boolean> => {
  try {
    const formattedNumber = formatPhoneNumber(phoneNumber);
    
    if (!isValidPhoneNumber(formattedNumber)) {
      throw new Error('Invalid phone number format');
    }
    
    const settingsRef = doc(db, 'notificationSettings', userId);
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      // Update existing settings
      await updateDoc(settingsRef, {
        phoneNumber: formattedNumber,
        phoneNumberVerified: false, // Set to true after verification
        smsNotifications: enableSMS
      });
    } else {
      // Create default settings with phone number
      const defaultSettings: NotificationSettings = {
        userId,
        tripInvites: true,
        eventInvites: true,
        messages: true,
        comments: true,
        tripUpdates: true,
        eventUpdates: true,
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: enableSMS,
        phoneNumber: formattedNumber,
        phoneNumberVerified: false
      };
      
      await setDoc(settingsRef, defaultSettings);
    }
    
    return true;
  } catch (error) {
    console.error('Error saving phone number:', error);
    return false;
  }
};

/**
 * Start the verification process for a phone number
 * This would typically send a verification code via SMS
 */
export const startPhoneVerification = async (userId: string, phoneNumber: string): Promise<boolean> => {
  try {
    // In a real implementation, this would:
    // 1. Generate a verification code
    // 2. Store it in Firestore with an expiration time
    // 3. Send it via SMS to the user
    // 4. Return true if successful
    
    // Mock implementation

    return true;
  } catch (error) {
    console.error('Error starting phone verification:', error);
    return false;
  }
};

/**
 * Verify a phone number with a code the user received
 */
export const verifyPhoneNumber = async (
  userId: string, 
  verificationCode: string
): Promise<boolean> => {
  try {
    // In a real implementation, this would:
    // 1. Check the verification code against what's stored
    // 2. Check if it's expired
    // 3. If valid, mark the phone as verified
    
    // Mock implementation - always succeeds with code "123456"
    if (verificationCode === '123456') {
      const settingsRef = doc(db, 'notificationSettings', userId);
      await updateDoc(settingsRef, {
        phoneNumberVerified: true
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error verifying phone number:', error);
    return false;
  }
}; 